
import { useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCollections } from '@/contexts/collections';
import { INBOX_ID } from '@/utils/contentFormUtils';

export const useExtensionSync = () => {
  const { user, session } = useAuth();
  const { collections } = useCollections();

  // Send authentication status to extension
  const syncAuthWithExtension = useCallback(() => {
    if (typeof window !== 'undefined') {
      const authData = {
        isAuthenticated: !!user,
        user: user ? {
          id: user.id,
          email: user.email
        } : null,
        session: session ? {
          access_token: session.access_token,
          refresh_token: session.refresh_token
        } : null
      };

      // Post message to any listening extensions
      window.postMessage({
        type: 'CLUSTR_AUTH_STATUS',
        payload: authData
      }, '*');

      console.log('[Extension Sync] Auth status sent to extension:', authData.isAuthenticated);
    }
  }, [user, session]);

  // Send collections list to extension with proper "In Box" handling
  const syncCollectionsWithExtension = useCallback(() => {
    if (typeof window !== 'undefined' && user) {
      // Create the "In Box" virtual collection - always first and marked as default
      const inboxCollection = {
        id: INBOX_ID,
        name: 'In Box',
        description: 'Unorganized content',
        color: '#6B7789',
        isVirtual: true,
        isDefault: true, // Mark as default for the extension
        position: -1 // Ensure it's always first
      };

      // Map real collections
      const realCollections = collections.map(collection => ({
        id: collection.id,
        name: collection.name,
        description: collection.description || '',
        color: collection.color || '#8E9196',
        isVirtual: false,
        isDefault: false,
        position: collection.position || 0
      }));

      // Combine with In Box first
      const collectionsWithInbox = [inboxCollection, ...realCollections];

      const payload = {
        collections: collectionsWithInbox,
        defaultCollectionId: INBOX_ID, // Explicitly set the default
        totalCount: collectionsWithInbox.length
      };

      window.postMessage({
        type: 'CLUSTR_COLLECTIONS_LIST',
        payload
      }, '*');

      console.log('[Extension Sync] Collections sent to extension:', {
        count: collectionsWithInbox.length,
        default: INBOX_ID,
        collections: collectionsWithInbox.map(c => ({ id: c.id, name: c.name, isDefault: c.isDefault }))
      });
    }
  }, [collections, user]);

  // Handle extension requests with better error handling
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        if (event.data?.type === 'CLUSTR_REQUEST_AUTH_STATUS') {
          console.log('[Extension Sync] Auth status requested by extension');
          syncAuthWithExtension();
        } else if (event.data?.type === 'CLUSTR_REQUEST_COLLECTIONS') {
          console.log('[Extension Sync] Collections requested by extension');
          syncCollectionsWithExtension();
        }
      } catch (error) {
        console.error('[Extension Sync] Error handling extension message:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [syncAuthWithExtension, syncCollectionsWithExtension]);

  // Auto-sync when auth changes
  useEffect(() => {
    syncAuthWithExtension();
  }, [syncAuthWithExtension]);

  // Auto-sync when collections change
  useEffect(() => {
    syncCollectionsWithExtension();
  }, [syncCollectionsWithExtension]);

  // Initial sync when hook mounts
  useEffect(() => {
    // Small delay to ensure everything is initialized
    const timer = setTimeout(() => {
      console.log('[Extension Sync] Initial sync triggered');
      syncAuthWithExtension();
      syncCollectionsWithExtension();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return {
    syncAuthWithExtension,
    syncCollectionsWithExtension
  };
};
